import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { toast } from "sonner"
import { z } from "zod"
import { useSignInEmail } from "@/api/client/auth"
import logo from "@/assets/logo.png"
import { Button } from "@/components/ui/button"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { clearIpRestrictedMessage } from "@/features/ip-restriction"
import { useSessionRefetch } from "@/store/session"

const signinSchema = z.object({
  email: z
    .string()
    .min(1, "メールアドレスを入力してください")
    .email("有効なメールアドレスを入力してください"),
  password: z
    .string()
    .min(8, "パスワードは8文字以上である必要があります")
    .regex(/^(?=.*[a-zA-Z])(?=.*\d)[A-Za-z0-9]+$/, "パスワードは半角英数字で入力してください"),
})

type SigninFormData = z.infer<typeof signinSchema>

function getValidRedirectPath(redirect: string | null): string | undefined {
  if (redirect?.startsWith("/") && !redirect.startsWith("//")) {
    return redirect
  }
  return undefined
}

export function SigninPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const redirectTo = getValidRedirectPath(searchParams.get("redirect"))
  const refetchSession = useSessionRefetch()
  const form = useForm<SigninFormData>({
    resolver: zodResolver(signinSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  })

  const { mutateAsync: signInEmail, isPending } = useSignInEmail()

  const onSubmit = async (data: SigninFormData) => {
    try {
      await signInEmail(data)
      clearIpRestrictedMessage()
      toast.success("ログインに成功しました")
      await refetchSession()
      navigate(redirectTo || "/")
    } catch (error) {
      console.error("Sign in error:", error)
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <div className="flex-1 flex flex-col items-center px-4 pb-8 py-10">
        <div className="w-full max-w-sm mb-10 text-center">
          <div className="flex justify-center mb-4">
            <img src={logo} alt="AGENTCALL エージェントコール" className="h-auto w-full" />
          </div>
          <p className="text-sm text-slate-500 leading-relaxed">管理者向けポータルサイト</p>
        </div>

        <h2 className="text-lg font-bold text-slate-700 mb-10">管理者アカウントにログイン</h2>

        <div className="w-full max-w-sm space-y-10">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-semibold text-slate-600">
                      メールアドレス
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="name@company.jp"
                        type="email"
                        autoComplete="email"
                        className="h-11"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-sm font-semibold text-slate-600">
                      パスワード
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder="*****"
                        type="password"
                        autoComplete="current-password"
                        className="h-11"
                        {...field}
                      />
                    </FormControl>
                    <p className="text-sm text-slate-400">
                      半角英数字、8文字以上で入力してください
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="space-y-4 pt-3">
                <Button
                  type="submit"
                  size="lg"
                  className="w-full h-11 font-bold"
                  disabled={form.formState.isSubmitting}
                  loading={isPending}
                >
                  ログイン
                </Button>
              </div>

              <div className="text-center">
                <Link
                  to="/password-issue"
                  className="text-sm font-bold text-slate-400 hover:text-slate-500"
                >
                  パスワードを忘れた場合はこちらから
                </Link>
              </div>
            </form>
          </Form>
        </div>
      </div>
    </div>
  )
}
